/* Emissões → Processar documentos: link de pasta (POST /envios/link) ou arquivos (POST /envios/documentos). */

import { api } from '../../core/api';
import { cat, state } from '../../core/estado';
import { esc } from '../../core/formato';
import { $, ic, valorDe } from '../../ui/dom';
import { toast } from '../../ui/toast';
import { ir, render } from '../../roteador';
import type { Pagina } from '../pagina';

/** Obra (obrigatória) e tipo de documento (opcional), comuns ao envio por link e por arquivo. */
function destinoHtml(): string {
  const obras = cat().obras;
  const tipos = cat().tipos_documento || [];
  return `<div class="grid g2" style="gap:0 14px">
        <div class="field"><label for="docObra">Obra <span class="req">*</span></label>
          <select class="inp" id="docObra"><option value="">Selecione…</option>${obras.map((o) => `<option value="${esc(o.id)}" ${o.id === state.docObra ? 'selected' : ''}>${esc(o.nome)}</option>`).join('')}</select>
          <div class="hint">A obra a que esses documentos se referem.</div></div>
        <div class="field"><label for="docTipo">Tipo de documento</label>
          <select class="inp" id="docTipo"><option value="">Não sei / vários</option>${tipos.map((t) => `<option value="${esc(t.id)}" ${t.id === state.docTipo ? 'selected' : ''}>${esc(t.nome)}</option>`).join('')}</select>
          <div class="hint">Opcional. Ajuda a equipe ZNIT a lançar na categoria certa.</div></div>
      </div>`;
}

/** Lê obra e tipo; marca a obra se estiver vazia. */
function lerDestino(): { id_unidade: string; tipo_documento: string } | null {
  const id_unidade = valorDe('docObra');
  const tipo_documento = valorDe('docTipo');
  state.docObra = id_unidade;
  state.docTipo = tipo_documento;
  const campo = document.getElementById('docObra');
  campo?.classList.toggle('err', !id_unidade);
  if (!id_unidade) {
    toast('Escolha a obra a que os documentos se referem.');
    return null;
  }
  return { id_unidade, tipo_documento };
}

async function enviar(botao: HTMLElement, chamada: () => Promise<unknown>, mensagem: string): Promise<void> {
  botao.classList.add('dis');
  try {
    await chamada();
    toast(mensagem);
    ir('#/emissoes/envios');
  } catch (e) {
    toast((e as Error).message || 'Não foi possível enviar.');
  } finally {
    botao.classList.remove('dis');
  }
}

const formularioLink = () => `
        <h2 class="sec">Apontar pasta compartilhada</h2>
        <div class="field"><label for="docLink">Link do OneDrive ou SharePoint</label>
          <input class="inp" id="docLink" placeholder="https://cte.sharepoint.com/sites/Obras/..."></div>
        <div class="field" style="margin-bottom:22px"><label for="docPath">Caminho específico dentro da pasta (opcional)</label>
          <input class="inp" id="docPath" placeholder="Ex.: /Residencial Aurora/Notas Fevereiro"></div>
        <button class="btn btn-primary" data-clique="enviarLink">Enviar para processamento</button>`;

const formularioArquivos = () => `
        <h2 class="sec">Enviar arquivos brutos</h2>
        <div class="drop" id="dropDoc" data-clique="escolher">
          ${ic('i-upload')}<div><u>Envie</u> ou arraste os arquivos aqui</div><span class="fmt">PDF, PNG, JPG, XML — até 20 arquivos de 20 MB</span>
        </div>
        <input type="file" id="fileDoc" accept=".pdf,.png,.jpg,.jpeg,.xml" multiple style="display:none" data-muda="arquivos">
        <div id="fileDocList" style="margin-top:12px;display:flex;flex-direction:column;gap:8px"></div>
        <button class="btn btn-primary" style="margin-top:18px" data-clique="enviarArquivos">Enviar para processamento</button>`;

export const documentos: Pagina = {
  render() {
    const modo = state.docModo;
    state.filesDoc = [];
    const icone = (n: string) => ic(n, 'width:15px;height:15px;vertical-align:-3px');
    return `
  <h1 class="page">Processar documentos</h1>
  <p class="sub">Envie notas fiscais, faturas e outros documentos brutos. A ZNIT extrai os dados e atualiza o inventário — sem você precisar classificar nada manualmente.</p>
  <div class="seg" style="margin-bottom:22px">
    <button class="${modo === 'link' ? 'on' : ''}" data-clique="modo" data-modo="link">${icone('i-link')} Link do OneDrive/SharePoint</button>
    <button class="${modo === 'upload' ? 'on' : ''}" data-clique="modo" data-modo="upload">${icone('i-upload')} Enviar arquivos</button>
  </div>
  <div class="grid g2" style="align-items:start">
    <div class="card">${destinoHtml()}${modo === 'link' ? formularioLink() : formularioArquivos()}</div>
    <div class="callout info">${ic('i-mail')}
      <div><b>Como funciona o processamento?</b><br>O link ou os arquivos ficam registrados para a equipe ZNIT (os arquivos, em um armazenamento privado). Fazemos a leitura dos documentos, aplicamos os fatores de emissão certos e atualizamos o inventário. Acompanhe tudo em <a class="btn-plain" style="font-weight:700;text-decoration:underline" href="#/emissoes/envios">Meus envios</a>.</div>
    </div>
  </div>`;
  },

  acoes: {
    modo(el) {
      state.docObra = valorDe('docObra');
      state.docTipo = valorDe('docTipo');
      state.docModo = el.dataset.modo === 'upload' ? 'upload' : 'link';
      render();
    },

    escolher: () => document.getElementById('fileDoc')?.click(),

    arquivos(el) {
      const lista = Array.from((el as HTMLInputElement).files || []);
      state.filesDoc = lista;
      $('#fileDocList').innerHTML = lista.map((f) => `<div class="file-chip">${ic('i-doc')}${esc(f.name)} <small>${(f.size / 1024).toFixed(0)} KB</small></div>`).join('');
      $('#dropDoc').classList.toggle('has-files', lista.length > 0);
    },

    enviarLink(el) {
      const destino = lerDestino();
      if (!destino) return;
      const link = valorDe('docLink');
      if (!link) return toast('Cole o link da pasta antes de enviar.');
      return enviar(el, () => api('POST', '/envios/link', { body: { link, caminho: valorDe('docPath'), ...destino } }),
        'Link enviado. A equipe ZNIT vai processar os documentos da pasta.');
    },

    enviarArquivos(el) {
      const destino = lerDestino();
      if (!destino) return;
      const arquivos = state.filesDoc;
      if (!arquivos.length) return toast('Escolha os arquivos antes de enviar.');
      const fd = new FormData();
      arquivos.forEach((f) => fd.append('arquivos', f, f.name));
      fd.append('id_unidade', destino.id_unidade);
      if (destino.tipo_documento) fd.append('tipo_documento', destino.tipo_documento);
      return enviar(el, () => api('POST', '/envios/documentos', { form: fd }),
        `${arquivos.length} arquivo(s) enviado(s). A equipe ZNIT vai processar os documentos.`);
    },
  },
};
